import { useState } from "react";
import { downloadPathCertificate } from "../api";
import { useToast } from "./ToastContext";
import { getCurrentSelectedTenant } from "../utils/user";
import { getRailsClient } from "../client";

interface CertificateRowData {
  roleEnrollmentPublicId?: string;
  emailUrl?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
}

export function useCertificateActions() {
  const { showToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const getCertificateUrl = (roleEnrollmentPublicId: string): string | null => {
    const tenantName = getCurrentSelectedTenant();

    if (!roleEnrollmentPublicId || !tenantName) return null;

    const origin = window.location.origin;

    return `${origin}/api/certificates/${roleEnrollmentPublicId}/${tenantName}`;
  };

  const sendCertificateToEmail = async (url: string): Promise<void> => {
    try {
      await getRailsClient().get(url);
    } catch (error) {
      console.error("Error sending certificate via email:", error);
    }
  };

  const onOpenLink = (link: string) => () => {
    window.open(link, "_blank", "noopener, noreferrer");
  };

  const viewCertificate = (rowData: CertificateRowData) => {
    if (!rowData.roleEnrollmentPublicId) return;
    const certificateUrl = getCertificateUrl(rowData.roleEnrollmentPublicId);
    if (!certificateUrl) return;

    window.open(certificateUrl, "_blank", "noopener, noreferrer");
  };

  const copyCertificateLink = (rowData: CertificateRowData) => {
    if (!rowData.roleEnrollmentPublicId) return;
    const url = getCertificateUrl(rowData.roleEnrollmentPublicId);
    if (!url) return;

    navigator.clipboard.writeText(url);
    showToast({
      severity: "success",
      summary: "Success",
      detail: "Certificate link copied to clipboard",
      life: 3000,
    });
  };

  const sendCertificateEmail = async (rowData: CertificateRowData) => {
    const url = rowData.emailUrl;
    if (!url) return;

    try {
      await sendCertificateToEmail(url);
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Certificate sent to your email",
        life: 3000,
      });
    } catch (error) {
      console.error("Error sending certificate email:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to send certificate",
        life: 3000,
      });
    }
  };

  const handleCertificateDownload = async (certificateId: number) => {
    setIsDownloading(true);

    try {
      await downloadPathCertificate(certificateId);
    } catch (error) {
      console.error("Error downloading certificate:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    handleCertificateDownload,
    viewCertificate,
    copyCertificateLink,
    sendCertificateEmail,
    onOpenLink,
    isDownloading,
  };
}
